// PartnerSuite.jsx
import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  Chip,
  useTheme,
  styled
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import API from '../../../API';
import routes from '../../../Config/routes';
import { ConfigValue } from '../../../Config';
import ChatModalCreator from '../../../Components/ChatComponents/creatorchatmodal';

/* ───────── styled helpers ───────── */
const Page = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: theme.palette.background.default
}));
const Dashboard = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(4),
  maxWidth: 1200,
  marginInline: 'auto'
}));
const ActionCard = ({ title, children }) => (
  <Card elevation={4} sx={{ height: '100%' }}>
    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

/* ───────── helper fetchers (unchanged) ───────── */
const fetchUsersByCompany = async () => {
  const res = await fetch(
    `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/creatorUsers/users_by_company`
  );
  const raw = await res.json();
  const cleaned = {};
  Object.keys(raw).forEach((c) => {
    const users = raw[c].filter(
      (u) => u.email && !u.email.endsWith('@gmail.com')
    );
    if (users.length) cleaned[c] = users;
  });
  return cleaned;
};
const fetchContacts = async () => {
  const res = await fetch(
    'https://blitz-backend-nine.vercel.app/api/crm/contacts'
  );
  const raw = await res.json();
  return raw.filter((c) => c.email && !c.email.endsWith('@gmail.com'));
};

/* ───────── component ───────── */
export default function PartnerSuite() {
  const theme = useTheme();
  const navigate = useNavigate();

  /* ------------------------------------------------
   * 1.   Who am I?
   * ------------------------------------------------ */
  const {
    data: me,
    isLoading,
    error
  } = useQuery('creator_me', () => API.creatorConnect.getCreator(), {
    refetchOnWindowFocus: false
  });

  const { creator, refresh_token } = me?.user || {};
  const isPartner = creator?.status === 'Partner' || Boolean(refresh_token);

  /* ------------------------------------------------
   * 2.   Supervisor profile (with debug logging)
   * ------------------------------------------------ */
  const supUserId = creator?.superviser;
  const {
    data: supervisorRaw,
    isLoading: loadingSup,
    error: supErr
  } = useQuery(
    ['supervisorProfile', supUserId],
    () => API.users.fetchPublicDetails(supUserId),
    { enabled: !!supUserId }
  );

  // Normalize the API response: some endpoints return { user: ... }
  const supervisor = useMemo(() => {
    if (!supervisorRaw) return null;
    return supervisorRaw.user ?? supervisorRaw;
  }, [supervisorRaw]);

  // Debug: Log raw and normalized supervisor data
  useEffect(() => {
    console.log('---- DEBUG: supervisorRaw ----');
    console.log(supervisorRaw);
    console.log('---- DEBUG: normalized supervisor ----');
    console.log(supervisor);
  }, [supervisorRaw, supervisor]);

  /* ------------------------------------------------
   * 3.   Pipeline meta (unchanged)
   * ------------------------------------------------ */
  const { data: inboundMeta } = useQuery(
    'partner_inbound_meta',
    () => API.creatorConnect.getEmails({ page: 0, rows: 1 }),
    { enabled: isPartner }
  );
  const totalInbound = inboundMeta?.total ?? 0;

  const { data: usersByCompany } = useQuery(
    'usersByCompany',
    fetchUsersByCompany,
    { enabled: isPartner }
  );
  const { data: contacts } = useQuery('contacts', fetchContacts, {
    enabled: isPartner
  });

  const totalOutbound = useMemo(() => {
    if (!isPartner) return 0;
    const companies = Object.keys(usersByCompany || {}).length;
    const cc = (contacts || []).length;
    return companies + cc;
  }, [usersByCompany, contacts, isPartner]);

  /* ------------------------------------------------
   * 4.   Chat modal state
   * ------------------------------------------------ */
  const [chatOpen, setChatOpen] = useState(false);
  const openChat = () => supUserId && setChatOpen(true);

  /* ------------------------------------------------
   * 5.   Early returns (loading / errors / gating)
   * ------------------------------------------------ */
  if (isLoading) {
    return (
      <Page>
        <CircularProgress sx={{ m: 'auto', mt: 16 }} />
      </Page>
    );
  }
  if (error) {
    return (
      <Page>
        <Alert severity="error" sx={{ m: 4 }}>
          {error.response?.data?.error || error.message}
        </Alert>
      </Page>
    );
  }
  if (!isPartner) {
    return (
      <Page>
        <Alert
          severity="warning"
          sx={{ m: 'auto', mt: 12, textAlign: 'center', maxWidth: 600 }}
        >
          <Typography variant="h5" fontWeight={700} gutterBottom>
            You aren’t a Blitz Partner yet
          </Typography>
          This suite is only available to creators enrolled in the Partner
          Program.
        </Alert>
      </Page>
    );
  }

  /* ------------------------------------------------
   * 6.   Render
   * ------------------------------------------------ */
  const supName = supervisor
    ? `${supervisor.first_name ?? ''} ${supervisor.last_name ?? ''}`.trim() ||
      supervisor.username
    : creator?.superviser ?? '—';
  const supAvatar = supervisor?.phphref || creator?.superviser_pfphref || '';
  const supInitials = supName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2);

  return (
    <Page>
      <Dashboard>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{ mb: 4, textAlign: 'center' }}
        >
          Partner Suite
        </Typography>

        <Grid container spacing={4}>
          {/* ───────── Supervisor Card ───────── */}
          <Grid item xs={12} md={6}>
            <ActionCard title="Your Supervisor">
              {loadingSup && (
                <CircularProgress size={32} sx={{ alignSelf: 'center' }} />
              )}

              {!loadingSup && supErr && (
                <Alert severity="error">
                  Couldn’t load supervisor profile. ({supErr.message})
                </Alert>
              )}

              {!loadingSup && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Avatar
                      src={supAvatar}
                      alt={supName}
                      sx={{ width: 72, height: 72, bgcolor: 'primary.main' }}
                    >
                      {!supAvatar && supInitials}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="h5" fontWeight={700}>
                        {supName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Account Manager · Blitz
                      </Typography>
                    </Box>

                    {supUserId && (
                      <Tooltip title="Chat with your supervisor">
                        <IconButton color="primary" onClick={openChat}>
                          <ChatBubbleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {supervisor && (
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                      }}
                    >
                      {supervisor.email && (
                        <Chip
                          icon={<EmailIcon />}
                          label={supervisor.email}
                          variant="outlined"
                        />
                      )}

                      {supervisor.phone_number && (
                        <Chip
                          icon={<PhoneIcon />}
                          label={supervisor.phone_number}
                          variant="outlined"
                        />
                      )}

                      {supervisor.company?.name && (
                        <Chip
                          icon={<BusinessIcon />}
                          label={supervisor.company.name}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}

                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Your dedicated point‑of‑contact at Blitz. Reach out for
                    strategy, pricing advice, or anything you need to grow
                    faster.
                  </Typography>
                </>
              )}
            </ActionCard>
          </Grid>

          {/* ───────── Perks Card (unchanged) ───────── */}
          <Grid item xs={12} md={6}>
            <ActionCard title="Your Perks">
              {[
                'AI Sales Manager scouting 24/7',
                'Priority access to top‑tier brand briefs',
                'BlitzPay instant payouts',
                '1‑on‑1 strategy sessions with your supervisor'
              ].map((p) => (
                <Typography
                  key={p}
                  sx={{
                    '&::before': {
                      content: '"✓"',
                      color: theme.palette.success.main,
                      mr: 1
                    }
                  }}
                >
                  {p}
                </Typography>
              ))}
            </ActionCard>
          </Grid>

          {/* ───────── Pipeline Card (unchanged) ───────── */}
          <Grid item xs={12}>
            <ActionCard title="Pipeline Summary">
              <Typography variant="h6">
                Total Outbound available clients: <b>{totalOutbound}</b>
              </Typography>
              <Typography variant="h6">
                Total Inbound emails: <b>{totalInbound}</b>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate(routes.partnerSuiteOutgoing)}
                >
                  View Outbound
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate(routes.partnerSuiteIncoming)}
                >
                  View Inbound
                </Button>
              </Box>
            </ActionCard>
          </Grid>
        </Grid>
      </Dashboard>

      {/* ───────── Chat Modal ───────── */}
      <ChatModalCreator
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        recipientUserId={supUserId}
      />    
    </Page>
  );
}
